import React, { ReactElement, useContext, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis,
} from 'recharts';
import { LocaleContext } from '../../../shared/context/LocaleContext';
import useDimensions from '../../../shared/hooks/useDimensions';
import { MultiValuesNode, SingleValueNode } from '../../../types/chartDataTypes';
import theme, { toRgba } from '../../../shared/theme';
import {
  barColor, Container, FilterWrapper, StyledFilterCheckboxes, getBarSizes,
} from './BarChart.styles';
import { BarData, mapBarData } from './BarChart.helper';

export type OnBarEventFunction = (stackName: string, columnName: string) => void;

export type BarChartProps = {
  data: MultiValuesNode,
  keys: string[],
  highlightStack?: string,
  onDataHover?: OnBarEventFunction,
  onDataClick?: OnBarEventFunction,
  hideLabel?: boolean,
};

const CustomBarChart = ({
  data,
  keys,
  highlightStack,
  onDataHover,
  onDataClick,
  hideLabel,
}: BarChartProps): ReactElement => {
  const { formatter } = useContext(LocaleContext);

  const [filteredEntries, setFilteredEntries] = useState([] as string[]);

  const [ref, chartDimensions] = useDimensions();

  const barData: BarData = mapBarData(data, keys);

  const barSizes = getBarSizes(chartDimensions?.width);

  const barCategoryGap = chartDimensions?.width
    ? chartDimensions.width - (4 * barSizes.barWidth + 2 * barSizes.barGap) : 20;

  const getStacksLabel = (props: any): any => {
    const { x, y } = props;
    // recharts removes the barGap when the categoryGap is 12 or below.
    // different bar width or gap size did not change that
    const barGap = barCategoryGap <= 12 ? 0 : barSizes.barGap;

    return data.entries.map((baseEntry, index) => {
      const delta = barGap / 2 - (barSizes.labelWidth - barSizes.barWidth) / 2;
      const pos = index === 0 ? x - barSizes.labelWidth - delta : x + delta;

      return (
        <foreignObject
          key={baseEntry.totalLabel || baseEntry.name}
          x={pos}
          y={y}
          height={barSizes.labelHeight}
          width={barSizes.labelWidth}
        >
          <div className="bar-name" style={{ width: barSizes.labelWidth }}>
            {baseEntry.name || baseEntry.totalLabel}
          </div>
        </foreignObject>
      );
    });
  };

  return (
    <Container ref={ref}>
      <ResponsiveContainer width="100%" height={350}>
        <BarChart
          data={barData.years}
          barSize={barSizes.barWidth}
          barCategoryGap={barCategoryGap}
          barGap={barSizes.barGap}
        >
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={getStacksLabel}
            scale="band"
            interval={0}
            height={barSizes.labelHeight}
          />
          {barData.stackEntries.reduce((result, stackEntries, index) => {
            const stack = index.toString();
            // Each stack will get an empty bar, that ensures the stack stays in the graph
            // when the filter has removed all stack entries
            result.push((
              <Bar
                key={`${stack}_empty`}
                stackId={stack}
                dataKey="none"
                label={undefined}
                fill="transparent"
              />
            ));

            stackEntries.forEach((stackEntry, index2) => {
              if (filteredEntries.includes(stackEntry.name)) {
                return;
              }

              const fillBaseColor = highlightStack
              && (highlightStack === stackEntry.name || stackEntry.groupNames.includes(highlightStack))
                ? theme.colors.lightGreen
                : barColor;

              const color = highlightStack === stackEntry.name
                ? fillBaseColor
                : toRgba(fillBaseColor, 1 - index2 * theme.opacities.barChartStep);

              const getBarValue = (node: SingleValueNode): number => {
                const match = node.entries.find((entry) => (entry.name === stackEntry.name));
                return match ? match.value : 0;
              };

              const renderCustomizedLabel = ({
                x, y, height, width, name,
              }): SVGElement => {
                const year = barData.years.find((aYear) => aYear.name === name);
                const entry = year.entries.find((anEntry) => anEntry.name === stackEntry.name);
                return (
                  <text
                    x={x + width / 2}
                    y={y + height / 2}
                    width={width}
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{ pointerEvents: 'none' }}
                  >
                    {formatter.formatNumber(entry.value)}
                  </text>
                ) as unknown as SVGElement;
              };

              const onSelectBar = (props: any): void => {
                // eslint-disable-next-line react/prop-types
                const { name } = props;
                if (onDataHover) {
                  onDataHover(stackEntry.name, name);
                }
              };

              const onDeselectBar = (): void => {
                if (onDataHover) {
                  onDataHover(null, null);
                }
              };

              const onClickBar = (props: any): void => {
                // eslint-disable-next-line react/prop-types
                const { name } = props;
                if (onDataClick) {
                  onDataClick(stackEntry.name, name);
                }
              };

              result.push((
                <Bar
                  key={`${stack}_${stackEntry.name}`}
                  stackId={stack}
                  dataKey={getBarValue}
                  label={hideLabel ? undefined : renderCustomizedLabel}
                  fill={color}
                  onMouseOver={onSelectBar}
                  onMouseOut={onDeselectBar}
                  onClick={onClickBar}
                  style={{ cursor: 'pointer' }}
                />
              ));
            });

            return result;
          }, [] as ReactElement[])}
        </BarChart>
      </ResponsiveContainer>

      <FilterWrapper>
        <StyledFilterCheckboxes
          stackEntries={barData.stackEntries}
          setRemovedEntries={setFilteredEntries}
        />
      </FilterWrapper>
    </Container>
  );
};

export default CustomBarChart;
